import { Stack, useTheme } from '@mui/material';

import { AddAutomationButton } from './components/AddAutomationButton';
import { AutomationListCTA } from './components/AutomationListCTA';
import { AutomationListProps, Automation } from '../types';
import { ATSAutomationListTestIds } from 'data-testids/ATS';
import { useAutomation } from '../context/AutomationContext';
import { AutomationListVariant, AutomationModalPaneOptions } from '../enums';
import { AutomationListItemSkeleton, AutomationListItem } from './components/AutomationListItem';
import { AutomationContainer, AutomationQuestionText } from '../styles';
import { groupByQuestionId } from '../utils/groupByQuestionId';
import { useState, useEffect } from 'react';
import { SortableList } from 'sharedComponents/List/SortableList/SortableList';
import { ACTION_TYPES } from '../reducers/automationReducer';
import { DragHandle, SortableItem } from 'sharedComponents/List/SortableList/components';
import { DeleteAutomationsButton } from './components/DeleteAutomationsButton';
import { EditAutomationsButton } from './components/EditAutomationsButton';
import { useQueryGetAutomationsListData } from '../api/listQueries';
import { ErrorSnackbar, SuccessSnackbar } from 'sharedComponents/Snackbar';
import { DEFAULT_AUTOMATION_ERROR_MESSAGE } from 'hooks/errorHandling/errorHandlingHooks';
import { SuccessErrorAlertSeverity } from 'sharedComponents/Alert/types';
import { filterGeneralAutomations } from '../utils';

export const AutomationList: React.FC<AutomationListProps> = ({
  variant,
  automationState,
  automationDispatch,
}) => {
  const theme = useTheme();
  const { onChangeModalPane, questions, roleId, setSelectedQuestionId } = useAutomation();
  const generalAutomations = automationState.automations
    ? filterGeneralAutomations(automationState.automations)
    : [];
  const sortedAutomations = generalAutomations && groupByQuestionId(generalAutomations);
  const { isLoading } = useQueryGetAutomationsListData(roleId);
  const groupedAutomations =
    sortedAutomations &&
    Object.entries(sortedAutomations).sort(([, automationsA], [, automationsB]) => {
      // Sort groups by the order value (all automations in a group have the same order)
      const orderA = automationsA[0]?.order || 0;
      const orderB = automationsB[0]?.order || 0;
      return orderA - orderB;
    });
  const [reorderedGroups, setReorderedGroups] = useState<[string, Automation[]][] | null>(null);
  const [currentToast, setCurrentToast] = useState<SuccessErrorAlertSeverity | null>(null);

  useEffect(() => {
    if (
      reorderedGroups &&
      groupedAutomations &&
      reorderedGroups.length > groupedAutomations.length
    ) {
      setReorderedGroups(null);
    }
  }, [groupedAutomations, reorderedGroups]);
  const isEditable = variant === AutomationListVariant.EDITABLE;

  const automationGroups = reorderedGroups || groupedAutomations || [];

  const sortableItems = automationGroups.map(([questionId, automations], index) => ({
    id: `question-${questionId}`,
    index,
    questionId,
    automations,
  }));
  const handleGroupsChange = (newItems: typeof sortableItems) => {
    const newGroups: [string, Automation[]][] = newItems.map((item) => [
      item.questionId,
      item.automations,
    ]);

    setReorderedGroups(newGroups);

    const updatedAutomations: Automation[] = [];
    let globalOrder = 0;
    newItems.forEach((item) => {
      item.automations.forEach((automation) => {
        updatedAutomations.push({
          ...automation,
          order: globalOrder++,
        });
      });
    });

    if (updatedAutomations.length > 0) {
      automationDispatch({
        type: ACTION_TYPES.SET_AUTOMATION_LIST,
        payload: {
          automations: updatedAutomations,
          hasUnsavedChanges: true,
        },
      });
    }
  };

  const handleSetCurrentToast = (variant: SuccessErrorAlertSeverity | null) =>
    setCurrentToast(variant);

  return (
    <Stack direction="column" data-testid={ATSAutomationListTestIds.LIST_PANE}>
      <Stack alignItems="flex-start">
        {isLoading ? (
          <>
            <AutomationListItemSkeleton />
            <AutomationListItemSkeleton />
          </>
        ) : (
          <SortableList
            items={sortableItems}
            onChange={handleGroupsChange}
            itemRenderer={(item) => (
              <SortableItem key={item.id} id={item.id} isEditable={isEditable}>
                <AutomationContainer
                  direction="row"
                  gap={theme.spacing(3)}
                  sx={{ position: 'relative' }}
                >
                  {variant === AutomationListVariant.EDITABLE && (
                    <DragHandle
                      sx={{
                        opacity: theme.palette.action.disabledOpacity,
                        maxHeight: theme.spacing(3.75),
                        alignSelf: 'flex-start',
                        marginTop: theme.spacing(1.25),
                      }}
                    />
                  )}
                  <Stack direction="column" sx={{ width: '100%' }}>
                    <AutomationQuestionText listVariant={variant}>
                      {questions.find((q) => q.id === parseInt(item.questionId))?.prompt}
                    </AutomationQuestionText>
                    {item.automations
                      .sort((a, b) => a.order - b.order)
                      .map((automation, index) => (
                        <AutomationListItem
                          variant={variant}
                          key={automation.id || `temp-${item.questionId}-${index}`}
                          automation={automation}
                          localIndex={index}
                        />
                      ))}
                  </Stack>
                  {variant === AutomationListVariant.EDITABLE && (
                    <Stack
                      direction="row"
                      sx={{
                        margin: theme.spacing(1),
                        zIndex: theme.zIndex.fab,
                        gap: theme.spacing(1),
                      }}
                    >
                      <EditAutomationsButton
                        questionId={parseInt(item.questionId)}
                        onEdit={() => {
                          setSelectedQuestionId(parseInt(item.questionId));
                          onChangeModalPane(AutomationModalPaneOptions.CREATE);
                        }}
                      />
                      <DeleteAutomationsButton
                        questionId={parseInt(item.questionId)}
                        onDelete={() =>
                          automationDispatch({
                            type: ACTION_TYPES.SET_AUTOMATION_LIST,
                            payload: {
                              automations: automationState.automations?.filter(
                                (automation) =>
                                  automation.trigger.question_id !== parseInt(item.questionId)
                              ),
                              hasUnsavedChanges: true,
                            },
                          })
                        }
                      />
                    </Stack>
                  )}
                </AutomationContainer>
              </SortableItem>
            )}
          />
        )}
        {isEditable && (
          <AddAutomationButton
            onClick={() => onChangeModalPane(AutomationModalPaneOptions.CREATE)}
          />
        )}
      </Stack>
      {isEditable && (
        <AutomationListCTA
          automationState={automationState}
          automationDispatch={automationDispatch}
          handleSetCurrentToast={handleSetCurrentToast}
        />
      )}
      <SuccessSnackbar
        open={currentToast === 'success'}
        message="Successfully saved"
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSAutomationListTestIds.SUCCESS_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />
      <ErrorSnackbar
        open={currentToast === 'error'}
        message={DEFAULT_AUTOMATION_ERROR_MESSAGE}
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSAutomationListTestIds.ERROR_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />
    </Stack>
  );
};
